import React from 'react';
import { debounce } from 'lodash';
import { Button, Collapse } from 'reactstrap';
import Api from '../Api';
import HtmlHelpers from '../HtmlHelpers';
import Links from '../Links';
import ListFunctions from '../FunctionsList';
import CaretToggle from '../Icons/CaretToggle';
import WmReactTableServerSide from '../Layout/WmReactTableServerSide';

// =======================================
// Usage

// put `{this.externalIdTable()}` where you want it to display
// put `externalIdOpen: true,` in state
// put `externalIdLoading: true,` in state
// add `this.externalIdTableRef = React.createRef();` to constructor

// add the following methods, update storageName and type, set hideBottomHr to true or false

//   updateState = (data) => this.setState(data);
//   toggleExternalId = () => (
//     this.setState(prevState => (
//       { externalIdOpen: !prevState.externalIdOpen }
//     ))
//   )

//   externalIdTable = () => {
//     const state = this.state;
//     const storageName = 'YOUR_LOCAL_STORAGE_NAME';
//     const options = {
//       auth: this.props.auth,
//       storageName: storageName,
//       data: state.externalIdData || [],
//       pages: state.externalIdPages,
//       loading: state.externalIdLoading,
//       type: 'MODEL_NAME',
//       uuid: state.item.uuid,
//       tableRef: this.externalIdTableRef,
//       toggleExternalId: this.toggleExternalId,
//       open: state.externalIdOpen,
//       hideBottomHr: true,
//     }
//     return ExternalIds.getTable(this.updateState, options)
//   }
// =======================================

const verifyType = row => ['Inbound Grain Ticket Document Number', 'Outbound Grain Ticket Document Number'].includes(row.original.external_id_description);
const verifyBtn = (row, options) => {
  const uuid = row.original.uuid;
  const disabled = options.disabled[uuid] || false;
  return HtmlHelpers.isNotEmpty(row.original.removed_at) || options.verify === undefined || !verifyType(row) ? '' : /*#__PURE__*/React.createElement(Button, {
    outline: true,
    color: "secondary",
    size: "sm",
    onClick: () => options.verify(uuid, options.auth, options.state, options.updateState),
    disabled: disabled
  }, /*#__PURE__*/React.createElement("span", {
    className: "small"
  }, "verify"));
};
const ExternalIds = {};
ExternalIds.getData = async (options, updateState) => {
  const filter = {
    pageSize: 100,
    filtered: [{
      id: 'model_type_in',
      value: [options.type]
    }, {
      id: 'model_uuid_in',
      value: [options.uuid]
    }]
  };
  const apiOptions = {
    auth: options.auth,
    listApi: 'exports/external_ids',
    params: filter,
    tableState: HtmlHelpers.dig([options, 'tableRef', 'current', 'state']),
    storageName: options.storageName
  };
  const data = await ListFunctions.fetchData(apiOptions);
  const externalIdData = {
    externalIdData: data.data,
    externalIdPages: data.pages,
    externalIdLoading: false
  };
  updateState(externalIdData);
};
ExternalIds.getTable = (updateState, options) => /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement("h4", null, "External IDs (", options.loading ? 'loading' : options.data.length, ") \xA0\xA0\xA0", /*#__PURE__*/React.createElement(CaretToggle, {
  onClick: options.toggle,
  open: options.open
})), options.hideBottomHr ? '' : /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Collapse, {
  isOpen: options.open
}, /*#__PURE__*/React.createElement(WmReactTableServerSide, {
  ref: options.tableRef,
  className: "otto__short_table",
  pages: options.pages,
  data: options.data,
  loading: options.loading,
  columns: ExternalIds.getColumns(options, updateState),
  onFetchData: debounce(() => ExternalIds.getData(options, updateState), 500),
  getTrProps: (state, rowInfo, instance) => !rowInfo || HtmlHelpers.isEmpty(rowInfo.row.removed_at) ? {} : {
    style: {
      textDecoration: 'line-through'
    }
  }
})));
ExternalIds.reverseRecord = async (rowId, uuid, options, updateState) => Api.crudDelete(options.auth, 'exports/external_ids', {
  uuid
}).then(response => updateState({
  exportData: ExternalIds.setReverseMsgs(rowId, response, options)
}));
ExternalIds.setReverseMsgs = (rowId, response, options) => {
  const newData = Object.assign([], options.data);
  if (response.errors === undefined) {
    window.location.reload(false);
  } else {
    newData[rowId].reverse_msg = response.errors.base[0];
  }
  newData[rowId].can_reverse = false;
  return newData;
};
const reverseBtn = (data, onClick) => data.can_reverse !== true ? data.reverse_msg || '' : /*#__PURE__*/React.createElement(Button, {
  outline: true,
  className: "otto__margin_right",
  color: "secondary",
  size: "sm",
  onClick: onClick
}, /*#__PURE__*/React.createElement("span", {
  className: "small"
}, "Reverse"));
ExternalIds.getColumns = (options, updateState) => [{
  Header: 'System',
  accessor: 'system',
  className: 'text-left'
}, {
  Header: 'Description',
  accessor: 'external_id_description',
  className: 'text-left'
}, {
  Header: 'ID',
  accessor: 'external_id',
  className: 'text-left'
}, {
  id: 'can_update',
  Header: 'Can Update',
  accessor: d => d.can_update ? 'Yes' : 'No',
  className: 'text-center',
  filterable: false,
  width: 150
}, {
  id: 'last_checked_at',
  Header: 'Last Checked At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.last_checked_at),
  className: 'text-right',
  filterable: false,
  width: 200
}, {
  id: 'removed_at',
  Header: 'Removed At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.removed_at),
  Cell: row => {
    const reverseOnClick = () => ExternalIds.reverseRecord(row.index, row.original.uuid, options, updateState);
    const removed_at = HtmlHelpers.format_utc_date_time(row.original.removed_at);
    return HtmlHelpers.isEmpty(removed_at) ? reverseBtn(row.original, reverseOnClick) : removed_at;
  },
  className: 'text-right',
  filterable: false,
  width: 200
}, {
  id: 'action',
  Header: '',
  Cell: row => verifyBtn(row, options),
  className: 'text-center',
  filterable: false,
  width: 200,
  show: options.verify !== undefined
}];
export default ExternalIds;